import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Badge from '../ui/Badge';
import InfoTip from '../ui/InfoTip';

/**
 * Level 3 of the "Contrôle qualité" drill-down: the content of one visit, section by
 * section, each section's measured time read against how the project fills that same
 * section over the same period.
 *
 * That comparison is the point, and it cuts both ways. A section clocked at zero
 * minutes is only notable when peers normally spend time there — on project 40922,
 * 55 to 97 % of visits leave most sections at zero, so a promoter doing the same is
 * following the project's practice. Conversely a section holding far more time than
 * peers ever spend there is where a clock jump landed.
 *
 * Prefilled read-only answers (store name, address, the declared duration echoed
 * back) are shown greyed and excluded from the counts: they are the app's, not the
 * promoter's, and counting them would flatter every visit.
 */

const TYPE_LABEL = { 1: 'texte', 2: 'nombre', 3: 'choix', 4: 'choix', 5: 'choix', 6: 'oui/non', 7: 'date', 10: 'liste' };

// Timing verdicts come from the backend (classifySectionTiming) so the reading is
// identical in the API, the CLI validator and here.
const TIMING = {
  'unusually-long': {
    tone: 'violet',
    label: 'anormalement long',
    frame: 'border-violet-300 dark:border-violet-700 bg-violet-50/60 dark:bg-violet-900/10',
    tip: "Cette section a absorbé bien plus de temps que ce que les autres visites du projet y passent. C'est typiquement là qu'une horloge avancée pendant qu'un bloc était ouvert se retrouve encaissée par le chronomètre.",
  },
  'filled-without-time': {
    tone: 'amber',
    label: 'rempli sans temps mesuré',
    frame: 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10',
    tip: "Des réponses ont été enregistrées alors que le chronomètre y a mesuré moins d'une minute, et les autres visites du projet passent habituellement du temps sur cette section. À rapprocher du contenu avant toute conclusion.",
  },
  'project-practice': {
    tone: 'gray',
    label: 'pratique du projet',
    frame: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
    tip: "Section à zéro minute, mais la majorité des visites de ce projet font de même sur cette période. Ce n'est pas un écart imputable à cet intervenant.",
  },
  untimed: {
    tone: 'gray',
    label: 'non chronométrée',
    frame: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
    tip: "Cette section n'a pas de chronomètre : l'application n'a pas mesuré le temps qui y a été passé. Rien à en conclure.",
  },
  normal: { tone: null, label: null, frame: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800', tip: null },
};

const renderValue = (value) => {
  if (value === null || value === undefined) return <span className="text-gray-400 dark:text-gray-500">non renseignée</span>;
  if (value === true) return 'Oui';
  if (value === false) return 'Non';
  return String(value);
};

const round1 = (v) => (v === null || v === undefined ? null : Math.round(v * 10) / 10);

const NormLine = ({ norm }) => {
  if (!norm) return null;
  return (
    <span className="text-xs text-gray-500 dark:text-gray-400">
      norme projet : méd. {round1(norm.medianMin) ?? '—'} min · p90 {round1(norm.p90Min) ?? '—'} min
      {norm.pctAtZero !== null && norm.pctAtZero !== undefined && ` · ${Math.round(norm.pctAtZero)} % des visites à 0`}
      {' '}
      <InfoTip
        width="w-72"
        text={`Calculé sur les ${norm.nbVisits} visites du projet qui comportent cette section, sur la période affichée. C'est la seule référence interprétable : une section rapide sur un projet où tout le monde va vite ne dit rien.`}
      />
    </span>
  );
};

const Section = ({ section }) => {
  const timing = TIMING[section.timing] || TIMING.normal;
  // The two readings worth opening start expanded. Local state so a parent re-render
  // cannot re-open one the admin just collapsed.
  const [open, setOpen] = useState(section.timing === 'unusually-long' || section.timing === 'filled-without-time');
  const timed = section.chronoMin !== null && section.chronoMin !== undefined;

  return (
    <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} className={`rounded-xl border ${timing.frame}`}>
      <summary className="cursor-pointer list-none p-3 text-left">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="font-semibold text-sm text-gray-900 dark:text-white flex-1 min-w-[12rem]">
            {section.name}
          </span>

          {timed && (
            <Badge tone={section.chronoMin === 0 ? 'amber' : 'blue'}>{section.chronoMin} min mesurées</Badge>
          )}

          <span className="text-xs text-gray-500 dark:text-gray-400">
            {section.nbFilled} / {section.nbAnswers} renseignées
          </span>

          {timing.label && (
            <span className="inline-flex items-center gap-1">
              <Badge tone={timing.tone} dot={section.timing !== 'project-practice' && section.timing !== 'untimed'}>
                {timing.label}
              </Badge>
              <InfoTip width="w-80" text={timing.tip} />
            </span>
          )}
        </div>
        <div className="mt-1"><NormLine norm={section.norm} /></div>
      </summary>

      {section.answers.length > 0 && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
          {section.answers.map((a, i) => (
            <li key={i} className={`px-3 py-2 flex flex-wrap items-baseline gap-x-2 text-sm text-left ${a.readOnly ? 'opacity-50' : ''}`}>
              {a.item && <span className="text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[9rem]">{a.item}</span>}
              <span className="flex-1 min-w-[12rem] text-gray-600 dark:text-gray-300">{a.question}</span>
              <span className="text-[10px] uppercase tracking-wide text-gray-400">{TYPE_LABEL[a.type] || ''}</span>
              <span className={`font-medium ${a.filled ? 'text-gray-900 dark:text-white' : ''}`}>{renderValue(a.value)}</span>
              {a.readOnly && (
                <span className="text-[10px] uppercase tracking-wide text-gray-400" title="Pré-rempli par l'application, non saisi par l'intervenant">
                  pré-rempli
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
};

const VisitsQualityVisitContent = ({ content }) => {
  if (!content) return null;

  const long = content.sections.filter((s) => s.timing === 'unusually-long');
  const rushed = content.sections.filter((s) => s.timing === 'filled-without-time');
  const practice = content.sections.filter((s) => s.timing === 'project-practice');

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 text-left">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {content.store || '—'}
          <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
            {content.date ? format(new Date(content.date), 'EEEE d MMMM yyyy à HH:mm', { locale: fr }) : ''}
          </span>
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          {content.declared === null ? 'Durée déclarée inconnue' : `${content.declared} min déclarées`}
          {' · '}
          {content.chrono === null ? 'aucun temps mesuré' : `${content.chrono} min mesurées`}
          {' · '}
          {content.nbFilled} réponses renseignées sur {content.nbAnswers} possibles
        </p>

        {long.length > 0 && (
          <p className="mt-2 text-sm text-violet-800 dark:text-violet-200">
            {long.map((s) => `« ${s.name} » (${s.chronoMin} min)`).join(', ')} {long.length === 1 ? 'concentre' : 'concentrent'}
            {' '}beaucoup plus de temps que les autres visites du projet n'en passent sur cette section.
          </p>
        )}
        {rushed.length > 0 && (
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
            {rushed.length === 1 ? 'Une section porte' : `${rushed.length} sections portent`} des réponses sans temps
            mesuré, alors que le projet y passe habituellement du temps.
          </p>
        )}
        {practice.length > 0 && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {practice.length} {practice.length === 1 ? 'autre section est' : 'autres sections sont'} à zéro minute, comme
            la majorité des visites de ce projet : à ne pas lire comme un écart de cet intervenant.
          </p>
        )}
      </div>

      {content.sections.map((section) => <Section key={section.name} section={section} />)}
    </div>
  );
};

export default VisitsQualityVisitContent;
